import React, { useEffect, useMemo, useState } from 'react'
import { CalendarIcon, DatePicker } from '@mui/x-date-pickers'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import { IconButton, CircularProgress } from '@material-ui/core'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import CancelIcon from '@mui/icons-material/Cancel'
import { updateInvoice } from './invoiceApi'
import { updateBillingRecordWithInvoice } from '../../../../../services/billingRecords.service'

const EditableDueDate = ({ dueDate, setDueDate, invoiceDetails, paymentTermsDays, billingRecord, invoiceDate, readOnly }) => {
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const invoice_id = invoiceDetails?.[0]?._id

  // Compute minimum due date (strictly after invoice date) in a robust way
  const minDueDate = useMemo(() => {
    if (!invoiceDate) return null
    // Use native Date to avoid dayjs parsing/timezone quirks
    const base = new Date(invoiceDate)
    const min = new Date(base)
    min.setDate(min.getDate() + 1)
    return min
  }, [invoiceDate])

  // If current dueDate is before minimum, clamp it automatically
  useEffect(() => {
    if (!minDueDate) return
    if (!dueDate) {
      setDueDate(dayjs(minDueDate).format('YYYY-MM-DD'))
      return
    }
    const current = new Date(dueDate)
    // compare by day (ignore time)
    const curYMD = new Date(current.getFullYear(), current.getMonth(), current.getDate())
    const minYMD = new Date(minDueDate.getFullYear(), minDueDate.getMonth(), minDueDate.getDate())
    if (curYMD < minYMD) {
      setDueDate(dayjs(minDueDate).format('YYYY-MM-DD'))
    }
  }, [minDueDate, dueDate, setDueDate])

  const handleEditClick = () => {
    // Keep state as string; DatePicker will be given dayjs(dueDate)
    setIsEditing(true)
  }

  const handleDateChange = (newValue) => {
    if (!newValue) return
    // newValue is a dayjs object
    if (!newValue.isValid()) return

    const pickedDate = newValue.toDate()

    // Enforce due date strictly after invoice date
    if (minDueDate) {
      const pickedYMD = new Date(pickedDate.getFullYear(), pickedDate.getMonth(), pickedDate.getDate())
      const minYMD = new Date(minDueDate.getFullYear(), minDueDate.getMonth(), minDueDate.getDate())
      if (pickedYMD < minYMD) return
    }
    setDueDate(newValue.format('YYYY-MM-DD'))
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      const payload = { dueDate: dayjs(dueDate).format('YYYY-MM-DD') }
      await updateInvoice(invoice_id, payload)
      //Also update billing record if it exists
      const recordId = billingRecord?.id || billingRecord?._id
      if (billingRecord && recordId) {
        await updateBillingRecordWithInvoice(recordId, {
          dueDate: payload.dueDate
        })
        console.log('Billing record due date updated')
      }
      setDueDate(dayjs(dueDate).format('YYYY-MM-DD'))
      setIsEditing(false)
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    setIsEditing(false)
  }

  // Display value; prefer provided dueDate, else derive from invoiceDate + terms
  const derivedDue = useMemo(() => {
    const terms = Number(paymentTermsDays) || 7
    try {
      const base = invoiceDate ? new Date(invoiceDate) : new Date()
      const derived = new Date(base)
      derived.setDate(derived.getDate() + terms)
      return dayjs(derived).format('YYYY-MM-DD')
    } catch (e) {
      return dayjs().add(terms, 'day').format('YYYY-MM-DD')
    }
  }, [invoiceDate, paymentTermsDays])

  const defaultDue = dayjs(dueDate || derivedDue).format('DD/MM/YYYY')

  return (
    <div className="flex items-center gap-2">
      {!isEditing ? (
        <>
          <span style={{ fontSize: '16px', fontWeight: 'bold' }}>
            : {defaultDue}
          </span>
          {!readOnly && (
            <IconButton className="p-1" size="small" onClick={handleEditClick}>
              <EditIcon fontSize="small" />
            </IconButton>
          )}
        </>
      ) : (
        <div className="flex items-center gap-2">
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={dueDate ? dayjs(dueDate) : null}
              onChange={handleDateChange}
              format="DD-MM-YYYY"
              // Prevent picking dates on/before invoice date
              minDate={minDueDate ? dayjs(minDueDate) : undefined}
              views={["day"]}
              openTo="day"
              slotProps={{
                textField: { size: 'small', className: 'w-32' },
              }}
              components={{
                OpenPickerIcon: CalendarIcon,
              }}
            />
          </LocalizationProvider>
          <div className="flex gap-1 relative">
            <IconButton
              className="p-1"
              size="small"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? (
                <CircularProgress size={16} className="absolute" />
              ) : (
                <SaveIcon className="h-4 w-4" />
              )}
            </IconButton>
            <IconButton
              className="p-1"
              size="small"
              onClick={handleCancel}
              disabled={isSaving}
            >
              <CancelIcon className="h-4 w-4" />
            </IconButton>
          </div>
        </div>
      )}
    </div>
  )
}

export default EditableDueDate
